import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { RecentlyProducts } from '../core/models/recentlyProducts';
import { ProductToCalculate } from '../core/models/productToCalculate';
import { TrackingObjects } from '../core/models/trakingObjects';
import { ReviewToSeller } from '../core/models/reviewToSeller';
import { Observable } from 'rxjs';
import { FavoriteList, Product as ProductFavorite } from '../core/models/favorites.model';
import { Location } from '../core/models/configurations';
import { LanceRequest } from '../core/models/bids';
import { NegotiationProduct } from '../pages/minha-conta/negociacoes-lances/negociacoes-lances.component';
import { SellerInfoResponse } from '../core/models/sellerInfoRequest';
import { Vehicle } from '../core/models/vehicle';
import { FiltroCompras, PaginaCompras } from '../pages/minha-conta/minhas-compras/minhas-compras.model';
import { DocumentosVenda, FiltroVendas, PaginaVendas, TipoDocumentoVenda } from '../pages/minha-conta/minhas-vendas/minhas-vendas.model';
import { AtualizacaoItemListaCompras, ConclusaoListaCompras } from './listas-compras-persistencia.model';
import { DecisaoRecompra, FreteRecompra, NovaNegociacaoRecompraRequest, PapelRecompra, Recompra, RespostaRecompra, ResumoRecompraToken } from './recompra.model';
import { KEEP_AUTH_REFRESH, SILENT_HTTP_ERRORS } from '../commons/http-interceptor.service';
import { AtualizacaoCompraRecorrente, CriacaoCompraRecorrente, DetalheCompraRecorrente, FiltroComprasRecorrentes, PaginaComprasRecorrentes } from '../core/models/compra-recorrente.model';

@Injectable({
  providedIn: 'root'
})
export class FunctionsService {
  constructor(
    private httpClient: HttpClient,
    private afAuth: AngularFireAuth
  ) { }

  base = `${environment.cloud_functions}/inversu`;

  produto(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/produtos/v1/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  produtos(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/produtos/v1?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  searchOneByTerm(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/produtos/v1/search-by-term?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  categoryChildren(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/category/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  productsByCategoryNameChildren(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/productsByCategoryNameChildren?${new HttpParams({ fromObject: request })}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );

  }

  contextualSearch(request: string): Observable<{
    status: number;
    error: string | null;
    message: string;
    data: { produtos: [], prompt_id: number, };
  }> {
    return this.httpClient.post<{
      status: number,
      error: string | null,
      message: string,
      data: { produtos: [], prompt_id: number },
    }>(
      `${this.base}/produtos/v1/contextual-search`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        term: request
      },
    );

  }

  /**
   * Upload de arquivo (Excel/CSV/PDF) com uma lista de compras corporativa.
   * `POST /produtos/v1/bulk-list-import` (multipart/form-data, campo `file`).
   * A IA extrai os itens e cruza cada um com o catálogo, devolvendo 0..N
   * candidatos por item (mesmo shape de produto de `searchOneByTerm`).
   * Não define `Content-Type` manualmente: o `HttpClient` gera o boundary do
   * multipart automaticamente ao detectar um corpo `FormData`.
   */
  bulkListImport(file: File): Observable<{
    status: number;
    error: string | null;
    message: string;
    data: {
      prompt_id: string;
      itens: {
        nome: string;
        quantidade: number;
        ean: string | null;
        valor_pretendido: number | null;
        candidatos: {
          ean: string;
          nome: string;
          imagem?: { url: string };
          negociacoes_ativas?: number;
          rating?: number;
          [key: string]: any;
        }[];
      }[];
    };
  }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.httpClient.post<{
      status: number,
      error: string | null,
      message: string,
      data: {
        prompt_id: string;
        itens: {
          nome: string;
          quantidade: number;
          ean: string | null;
          valor_pretendido: number | null;
          candidatos: {
            ean: string;
            nome: string;
            imagem?: { url: string };
            negociacoes_ativas?: number;
            rating?: number;
            [key: string]: any;
          }[];
        }[];
      },
    }>(
      `${this.base}/produtos/v1/bulk-list-import`,
      formData,
      {
        observe: 'body',
      }
    );
  }

  searchLocations(request: { searchTerm: string; selectedLocations?: Location[] }): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: Location[];
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: Location[];
    }>(
      `${this.base}/locations/v1/`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    )
  }

  searchNearestCities(request: { lat: Number; lng: Number }): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: Location[];
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: Location[]
    }>(
      `${this.base}/locations/v1/nearest`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    )
  }

  saveNegotiationRegion(request: { locations: Location[] }): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/clientes/v1/negotiations/region`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    )
  }

  marcas(request?: any) {
    return this.httpClient.get<any>(
      `${this.base}/marcascategorias/v1/marcas`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  adicionaMarca(request?: any) {
    return this.httpClient.post<any>(
      `${this.base}/marcascategorias/v1/marcas`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  produtosPopulares(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/produtos/v1/dashboard/populares?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  addProdutosPorEAN(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  adicionarClientes(request: any, token: string) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  atualizarCliente(request: any, token: string) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/update`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  profile() {
    return this.httpClient.get<any>(
      `${this.base}/clientes/v1/profile`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  meusProdutos() {
    return this.httpClient.get<any>(
      `${this.base}/clientes/v1/produtos`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  addNegociacao(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/negociacoes/v1`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  addNegociacaoComposta(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/negociacoes/v1/compound/${request.prompt_id}`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  detalheNegociacao(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/negociacoes/v1/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  negociacoes(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/negociacoes/v1?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Listagem paginada das compras do comprador logado, com filtro de situação,
   * período e busca textual. Substitui o uso de `negotiationsSealed` na tela de
   * Minhas Compras — aquela rota passa por `negociacoes` e não filtra nem conta.
   */
  minhasCompras(request: FiltroCompras) {
    const params = new HttpParams({
      fromObject: Object.entries(request).reduce((acc, [chave, valor]) => {
        if (valor !== null && valor !== undefined && valor !== '') acc[chave] = `${valor}`;
        return acc;
      }, {} as Record<string, string>)
    });

    return this.httpClient.get<{ data: PaginaCompras }>(
      `${this.base}/compras/v1?${params.toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Grava, por item, qual candidato do catálogo foi escolhido e a
   * negociação criada a partir dele — chamado pelo
   * `AdicionaNegociacaoCompostaComponent` durante a criação em lote.
   * `PATCH /listas-compras/v1/:id/itens/:index`.
   */
  atualizarItemListaCompras(id: string, index: number, request: AtualizacaoItemListaCompras): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.patch<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/listas-compras/v1/${id}/itens/${index}`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Marca a lista como concluída, registrando o resumo final da criação em
   * lote (total/sucesso/falhas) — chamado uma única vez, ao fim do
   * processamento de todos os lotes.
   * `POST /listas-compras/v1/:id/concluir`.
   */
  concluirListaCompras(id: string, resumo: ConclusaoListaCompras): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/listas-compras/v1/${id}/concluir`,
      resumo,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Listagem paginada das compras de rotina (recorrências de lista de
   * favoritos, E-44) do comprador logado, com filtro opcional de status.
   * `GET /compras-recorrentes/v1`.
   */
  listarComprasRecorrentes(request: FiltroComprasRecorrentes): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: PaginaComprasRecorrentes;
  }>> {
    const params = new HttpParams({
      fromObject: Object.entries(request).reduce((acc, [chave, valor]) => {
        if (valor !== null && valor !== undefined && valor !== '') acc[chave] = `${valor}`;
        return acc;
      }, {} as Record<string, string>)
    });

    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: PaginaComprasRecorrentes;
    }>(
      `${this.base}/compras-recorrentes/v1?${params.toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Detalhe de uma compra de rotina, com as últimas ocorrências.
   * `GET /compras-recorrentes/v1/:id`.
   */
  detalheCompraRecorrente(id: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: DetalheCompraRecorrente;
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: DetalheCompraRecorrente;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Cria uma nova compra de rotina — modo "parametrizado" (itens já
   * congelados, vindos do stepper) ou "cru" (itens informados manualmente a
   * partir de favoritos). `POST /compras-recorrentes/v1`.
   */
  criarCompraRecorrente(payload: CriacaoCompraRecorrente): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: string;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: string;
    }>(
      `${this.base}/compras-recorrentes/v1`,
      payload,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Atualiza periodicidade/término/pré-aviso/itens de uma compra de rotina
   * existente. `PUT /compras-recorrentes/v1/:id`.
   */
  atualizarCompraRecorrente(id: string, payload: AtualizacaoCompraRecorrente): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.put<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}`,
      payload,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /** `POST /compras-recorrentes/v1/:id/pausar`. */
  pausarCompraRecorrente(id: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}/pausar`,
      {},
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /** `POST /compras-recorrentes/v1/:id/retomar`. */
  retomarCompraRecorrente(id: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}/retomar`,
      {},
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /** `POST /compras-recorrentes/v1/:id/pular-proxima`. */
  pularProximaCompraRecorrente(id: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.post<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}/pular-proxima`,
      {},
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Encerramento (soft delete no backend: `status: 'encerrada'`).
   * `DELETE /compras-recorrentes/v1/:id`.
   */
  excluirCompraRecorrente(id: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: null;
  }>> {
    return this.httpClient.delete<{
      status: number;
      error: string | null;
      message: string;
      data: null;
    }>(
      `${this.base}/compras-recorrentes/v1/${id}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  negotiationsSealed(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/negociacoes/v1/sealed?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  getCompetitorPrice(request: string) {
    return this.httpClient.get<any>(
      `${this.base}/competitions/v1/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  getCountryByEANPrefix(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/countries/v1/by-ean?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }

    )
  }

  addProdutoCliente(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/produto`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  excluirProdutoCliente(request: any) {
    return this.httpClient.delete<any>(
      `${this.base}/clientes/v1/produto/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  atualizarProdutosPorEAN(request: any, token: string) {
    return this.httpClient.put<any>(
      `${this.base}/produtos/v1/${request.ean}`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  adicionaLance(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/negociacoes/v1/${request.uid}/lance`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  obtemLances(request: any) {
    return this.httpClient.get<{
      data: LanceRequest[]
    }>(
      `${this.base}/negociacoes/v1/${request.uid}/lances?${new HttpParams({ fromObject: request }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  dashboardNegotiationBids() {
    return this.httpClient.get<{
      data: NegotiationProduct[]
    }>(
      `${this.base}/negociacoes/v1/lances/dashboard-negotiation-bids`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  obtemLance(request: any) {
    return this.httpClient.get<{
      data: LanceRequest[]
    }>(
      `${this.base}/negociacoes/v1/${request.negociacao}/lance/${request.lance}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  obtemMeiosPagamento(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/negociacoes/v1/${request.negociacao}/lance/${request.lance}/frete/${request.frete}/meios-pagamento`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  checkout(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/vendas/v1`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  venda(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/vendas/v1/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  gerarRecibo(request: { uidVenda: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any | null }>(
      `${this.base}/correios/v1/prePostLabelAsyncPDF`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  gerarDadosRecibo(request: { uidVenda: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any | null }>(
      `${this.base}/correios/v1/prePostAsyncLabelDownload`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  gerarDeclaracaoConteudo(request: { uidVenda: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any | null }>(
      `${this.base}/correios/v1/contentDeclarationDownload`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  verRastreio(request: { codigoObjeto: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: TrackingObjects | null }>(
      `${this.base}/correios/v1/seeTrackingEvents`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  compra(request: any) {
    return this.httpClient.get<any>(
      `${this.base}/compras/v1/${request}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Listagem paginada das vendas do vendedor logado, com filtro de situação,
   * período e busca textual.
   */
  minhasVendas(request: FiltroVendas) {
    const params = new HttpParams({
      fromObject: Object.entries(request).reduce((acc, [chave, valor]) => {
        if (valor !== null && valor !== undefined && valor !== '') acc[chave] = `${valor}`;
        return acc;
      }, {} as Record<string, string>)
    });

    return this.httpClient.get<{ data: PaginaVendas }>(
      `${this.base}/vendas/v1?${params.toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /**
   * Etiqueta de postagem ou declaração de conteúdo de uma venda, sob demanda.
   * O base64 não vem na listagem — é o grosso do payload e só interessa no
   * clique.
   */
  documentoVenda(uidVenda: string, tipo: TipoDocumentoVenda) {
    return this.httpClient.get<{ data: DocumentosVenda }>(
      `${this.base}/vendas/v1/${uidVenda}/documentos/${tipo}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  /** Garante (idempotente) que a venda tenha etiqueta e declaração geradas. */
  gerarDocumentosVenda(uidVenda: string) {
    return this.httpClient.post<{ data: { documentos: { etiqueta: boolean; declaracao: boolean } } }>(
      `${this.base}/vendas/v1/${uidVenda}/documentos`,
      {},
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  fazerLoginComNovaSenha(email: string, senha: string) {
    this.afAuth.signInWithEmailAndPassword(email, senha)
      .then(() => {
        alert('Login com a nova senha bem-sucedido!');
        // Faça o que precisar após o login bem-sucedido
      })
      .catch((error) => {
        alert('Erro ao fazer login com a nova senha: ' + error);
      });
  }

  productAmountView(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/addView`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  clientAddHistoryView(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/updateHistoryView`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  clientAddFavoritesList(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/favorites/new-list`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  clientAddFavorite(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/favorites/`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  clientUpdateFavoriteLists(request: { ean: string, listNames: string[], myFavoriteToo?: boolean }) {
    return this.httpClient.put<any>(
      `${this.base}/clientes/v1/favorites/lists`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  clientAddFavoriteToList(request: any) {
    return this.httpClient.post<any>(
      `${this.base}/clientes/v1/favorites/list`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  clientRemoveFavorite(request: any) {
    return this.httpClient.put<any>(
      `${this.base}/clientes/v1/favorites/`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  getFavorites() {
    return this.httpClient.get<{
      data: {
        favorites: ProductFavorite[],
        lists: FavoriteList[]
      },
    }>(
      `${this.base}/clientes/v1/favorites`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )

  }

  getFavoritesListByName(request: { listName: string, }) {
    return this.httpClient.get<{
      data: FavoriteList,
    }>(
      `${this.base}/clientes/v1/favorites/lists/${request.listName}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )

  }

  getAllFavoriteEans() {
    return this.httpClient.get<{
      data: {
        eans: string[]
      },
    }>(
      `${this.base}/clientes/v1/favorites/eans`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  removeFavoritesList(request: any) {
    return this.httpClient.delete<any>(
      `${this.base}/clientes/v1/favorites/list`,
      {
        body: request,
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  renameFavoritesList(request: any) {
    return this.httpClient.put<any>(
      `${this.base}/clientes/v1/favorites/list`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  updateMostViewedProducts() {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/updateMostViewedProducts`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  updateSellersRating() {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/updateSellersRating`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  updateProductReviews() {
    return this.httpClient.post<any>(
      `${this.base}/produtos/v1/updateReviews`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    );
  }

  getRecentlyProduct() {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: RecentlyProducts[] }>(
      `${this.base}/produtos/v1/recentlyAll`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  updateRecentlyProduct(request: RecentlyProducts) {
    return this.httpClient.put<any>(
      `${this.base}/produtos/v1/recently/${request.id}`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  calculatePackageDimensions(request: ProductToCalculate) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any }>(
      `${this.base}/correios/v1/calculatePackageDimensions`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  sendReviewToSeller(request: ReviewToSeller) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any }>(
      `${this.base}/vendas/v1/review/seller`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  setSaleReadForPickup(request: { sale_id: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any }>(
      `${this.base}/vendas/v1/setSaleReadForPickUp`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  setSaleConfirmByClient(request: { sale_id: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any }>(
      `${this.base}/vendas/v1/setSaleConfirmByClient`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  setSaleConfirmBySeller(request: { sale_id: string }) {
    return this.httpClient.post<{ status: number, error: null | string, message: string, data: any }>(
      `${this.base}/vendas/v1/setSaleConfirmBySeller`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  identifyProduct(image: File, imageType: 'webp' | 'png' | 'jpg' | 'jpeg'): Observable<{
    status: number;
    error: string | null;
    message: string;
    data: { product_name: string; ean_code: string };
  }> {
    return this.httpClient.post<{
      status: number,
      error: null | string,
      message: string,
      data: { "product_name": string, "ean_code": string }
    }
    >(
      `${this.base}/produtos/v1/identify-product`,
      image,
      {
        observe: 'body',
        headers: new HttpHeaders({
          'Content-Type': `image/${imageType === 'jpg' ? 'jpeg' : imageType}`,
        })
      }
    )
  }

  salesListByEan(ean: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: { date: string; city: string; quantity: string; price: number; }[];
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: { date: string; city: string; quantity: string; price: number; }[];
    }
    >(
      `${this.base}/vendas/v1/list-by-ean/${ean}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  verifyChatIdByUserTelegram() {
    return this.httpClient.post<{
      status: number,
      error: null | string,
      message: string,
      data: {
        enabled: boolean,
      } | null,
    }
    >(
      `${this.base}/telegram/v1/verify-updates`,
      {
        observe: 'body',
        headers: new HttpHeaders({
          'Content-Type': `text/json`,
        })
      }
    )
  }

  enableAndDisableChatByUserTelegram(request: { enabled: boolean }) {
    return this.httpClient.post<{
      status: number,
      error: null | string,
      message: string,
      data: null,
    }
    >(
      `${this.base}/telegram/v1/enable-disable-chat-by-user`,
      request,
      {
        observe: 'body',
        headers: new HttpHeaders({
          'Content-Type': `application/json`,
        })
      }
    )
  }

  uploadPhotoClient(file: File) {
    // const formData = new FormData();
    // formData.append('photo', file, file.name);
    return this.httpClient.post<{
      status: number,
      error: null | string,
      message: string | null,
    }>(
      `${this.base}/clientes/v1/profile/photo`,
      file,
      {
        // observe: 'body',
        headers: new HttpHeaders({
          'Content-Type': `application/multiform-data`,
        })
      }
    )
  }

  removePhotoClient() {
    return this.httpClient.delete<{
      status: number,
      error: null | string,
      message: string | null,
    }>(
      `${this.base}/clientes/v1/profile/photo`,
      {
        observe: 'body',
        headers: new HttpHeaders({
          'Content-Type': `application/json`,
        })
      }
    )
  }

  sellerInfo(request: { uid: string }, options?: { context?: HttpContext }) {
    return this.httpClient.post<{
      data: SellerInfoResponse
    }>(
      `${this.base}/sellers/v1`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        context: options?.context,
      }
    );
  }

  addProductReview(request: { ean: string, rating: number, title: string, text: string, medias: { file: File, url: any }[] }) {
    return this.httpClient.post(
      `${this.base}/produtos/v1/review/add`,
      request,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      }
    )
  }

  searchBrandsVehicle(): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: [string];
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: [string];
    }>(`${this.base}/vehicles-parts/v1/brands`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }
  searchModelsVehicle(slug: string): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: [string];
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: [string];
    }>(`${this.base}/vehicles-parts/v1/models?slug=${slug}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }
  searchYearsVehicle({ slug, model }: { slug: string; model: string }): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: [string];
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: [string];
    }>(`${this.base}/vehicles-parts/v1/years?slug=${slug}&model=${model}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }
  searchVersionsVehicle({ slug, model, year }: { slug: string; model: string; year: number | string }): Observable<HttpResponse<{
    status: number;
    error: string | null;
    message: string;
    data: [string];
  }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: [string];
    }>(`${this.base}/vehicles-parts/v1/versions?slug=${slug}&model=${model}&year=${year}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }
  findVehicleParts(data:
    { slug: string; model: string; year: number | string, version: string, q?: string, page?: number, limit?: number, sort?: string }
  ):
    Observable<HttpResponse<{
      status: number;
      error: string | null;
      message: string;
      data: {
        id: string;
        products: [{
          avaliacoes: string; categoria: string; descricao: string; dimensoes: string; ean: string; ean_categoria_codigo: string; ean_categoria_descricao: string; especificacoes: string; keywords: string; marca: string; nome: string; observacoes: string; pais: string; rating: string; situacao: string; imagens: string; id: string;
        }],
        photo: string;
        totalCount: number;
      };
    }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: {
        id: string;
        products: [{
          avaliacoes: string; categoria: string; descricao: string; dimensoes: string; ean: string; ean_categoria_codigo: string; ean_categoria_descricao: string; especificacoes: string; keywords: string; marca: string; nome: string; observacoes: string; pais: string; rating: string; situacao: string; imagens: string; id: string;
        }],
        photo: string;
        totalCount: number;
      };
    }>(`${this.base}/vehicles-parts/v1/search?${new HttpParams({ fromObject: data }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }
  findVehicle(data:
    { slug: string; model: string; year: number | string, version: string }
  ):
    Observable<HttpResponse<{
      status: number;
      error: string | null;
      message: string;
      data: {
        id: string;
        photo: string;
      };
    }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: {
        id: string;
        photo: string;
      };
    }>(`${this.base}/vehicles-parts/v1/vehicle?${new HttpParams({ fromObject: data }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  findMineVehicles():
    Observable<HttpResponse<{
      status: number;
      error: string | null;
      message: string;
      data: {
        user_uid: string;
        updated_at: Date;
        veiculos: {
          vehicle_uid: string;
          marca: string;
          modelo: string;
          ano: string;
          versao: string;
          tipo: 'carro';
          imagem?: string;
          placa?: string;
          cor?: string;
          quilometragem?: number;
          observacoes?: string;
          isPrimary: boolean;
          updated_at: Date;
        }[];
        photo: string;
        totalCount: number;
      };
    }>> {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: {
        user_uid: string;
        updated_at: Date;
        veiculos: {
          vehicle_uid: string;
          marca: string;
          modelo: string;
          ano: string;
          versao: string;
          tipo: 'carro';
          imagem?: string;
          placa?: string;
          cor?: string;
          quilometragem?: number;
          observacoes?: string;
          isPrimary: boolean;
          updated_at: Date;
        }[];
        photo: string;
        totalCount: number;
      };
    }>(`${this.base}/vehicles/v1/mine`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  findMineCompatiblesPartsVehicles() {
    return this.httpClient.get<{
      status: number;
      error: string | null;
      message: string;
      data: string[];
    }>(`${this.base}/vehicles/v1/mine/compatibles-parts`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  saveVehicle(data: Vehicle) {
    return this.httpClient.post(`${this.base}/vehicles/v1`,
      data,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  deleteVehicle(vehicleUid: string) {
    return this.httpClient.delete(`${this.base}/vehicles/v1/${vehicleUid}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  searchCategoriesProducts(param: { descricao: string; limit: number; page: number; }) {
    return this.httpClient.get<{
      data: {
        categories: {
          _id: string;
          id: string;
          caminho: string;
          nivel: number;
          relacionamentos: string[];
          descricao: string;
          categoria_full: string;
          deleted_at: Date;
        }[];
        total: number;
      }
    }>(`${this.base}/categories-products/v1/search?${new HttpParams({ fromObject: param }).toString()}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  firstCategoriesProducts() {
    return this.httpClient.get<{
      data: {
        _id: string;
        id: string;
        caminho: string;
        nivel: number;
        relacionamentos: string[];
        descricao: string;
        categoria_full: string;
        deleted_at: Date;
      }[],
    }>(`${this.base}/categories-products/v1/first`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      });
  }

  subCategoriesProducts(id: string) {
    return this.httpClient.get<{
      data: {
        _id: string;
        id: string;
        caminho: string;
        nivel: number;
        relacionamentos: string[];
        descricao: string;
        categoria_full: string;
        deleted_at: Date;
      }[];
      total: number;
    }>(`${this.base}/categories-products/v1/subcategories/${id}`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      })
  }

  saveCategoriesClient(data: string[]) {
    return this.httpClient.post(`${this.base}/categories-clients/v1`,
      data,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      })
  }

  mineCategoriesClient() {
    return this.httpClient.get<{
      data: {
        _id: string;
        id: string;
        caminho: string;
        nivel: number;
        relacionamentos: string[];
        descricao: string;
        categoria_full: string;
        deleted_at: Date;
      }[];
    }>(`${this.base}/categories-clients/v1`,
      {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      })
  }

  // ---- Recompras (/recompras/v1) ----

  private recompraOpts(options?: { context?: HttpContext }, silent = false, autenticada = false) {
    // `silent`: a tela trata o erro (sem alert duplicado do interceptor).
    // Chamadas autenticadas mantêm o refresh de token em 401; as públicas não têm sessão.
    let context = silent
      ? (options?.context ?? new HttpContext()).set(SILENT_HTTP_ERRORS, true)
      : options?.context;
    if (silent && autenticada) context = context!.set(KEEP_AUTH_REFRESH, true);
    return {
      observe: 'response' as const,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      context,
    };
  }

  criarRecompra(request: { venda: string; quantidade: number; mutationId?: string }, options?: { context?: HttpContext }) {
    const body = { ...request, mutationId: request.mutationId ?? crypto.randomUUID() };
    return this.httpClient.post<{ data: { recompra: Recompra } }>(
      `${this.base}/recompras/v1`, body, this.recompraOpts(options, true, true));
  }

  listarRecompras(papel: PapelRecompra, options?: { context?: HttpContext }) {
    return this.httpClient.get<{ data: { recompras: Recompra[] } }>(
      `${this.base}/recompras/v1?papel=${encodeURIComponent(papel)}`, this.recompraOpts(options));
  }

  recompra(id: string, options?: { context?: HttpContext }) {
    return this.httpClient.get<{ data: { recompra: Recompra } }>(
      `${this.base}/recompras/v1/${encodeURIComponent(id)}`, this.recompraOpts(options));
  }

  cancelarRecompra(id: string, options?: { context?: HttpContext }) {
    return this.httpClient.post<{ data: { recompra: Recompra } }>(
      `${this.base}/recompras/v1/${encodeURIComponent(id)}/cancelar`, {}, this.recompraOpts(options, true, true));
  }

  responderRecompra(id: string, decisao: DecisaoRecompra, motivo?: string, options?: { context?: HttpContext }) {
    return this.httpClient.post<{ data: RespostaRecompra }>(
      `${this.base}/recompras/v1/${encodeURIComponent(id)}/responder`,
      motivo ? { decisao, motivo } : { decisao }, this.recompraOpts(options, true, true));
  }

  recalcularFretesRecompra(id: string, options?: { context?: HttpContext }) {
    return this.httpClient.post<{ data: { fretes: FreteRecompra[] } }>(
      `${this.base}/recompras/v1/${encodeURIComponent(id)}/fretes`, {}, this.recompraOpts(options, true, true));
  }

  novaNegociacaoDeRecompra(id: string, sobrescritas?: NovaNegociacaoRecompraRequest, options?: { context?: HttpContext }) {
    const body = { ...(sobrescritas ?? {}), mutationId: sobrescritas?.mutationId ?? crypto.randomUUID() };
    return this.httpClient.post<{ data: { negociacao: string } }>(
      `${this.base}/recompras/v1/${encodeURIComponent(id)}/nova-negociacao`, body, this.recompraOpts(options, true, true));
  }

  /** Público (sem login). Erros 404/409/410 ficam a cargo da tela. */
  resumoRecompraPorToken(token: string, options?: { context?: HttpContext }) {
    return this.httpClient.get<{ data: ResumoRecompraToken }>(
      `${this.base}/recompras/v1/resposta/${encodeURIComponent(token)}`, this.recompraOpts(options, true));
  }

  /** Público (sem login). Erros 404/409/410 ficam a cargo da tela. */
  responderRecompraPorToken(token: string, decisao: DecisaoRecompra, motivo?: string, options?: { context?: HttpContext }) {
    return this.httpClient.post<{ data: RespostaRecompra }>(
      `${this.base}/recompras/v1/resposta/${encodeURIComponent(token)}`,
      motivo ? { decisao, motivo } : { decisao }, this.recompraOpts(options, true));
  }

}
